import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Crown, Check, Star, Zap, Gift, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MembershipsPage = () => {
  const { token, user, updateUser } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);

  useEffect(() => {
    const fetchMemberships = async () => {
      try {
        const response = await fetch(`${API}/memberships`);
        if (response.ok) {
          setMemberships(await response.json());
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMemberships();
  }, []);

  const handleSubscribe = async (membershipId, planType) => {
    setSubscribing(`${membershipId}-${planType}`);
    try {
      const response = await fetch(`${API}/memberships/subscribe?membership_id=${membershipId}&plan_type=${planType}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Welcome to your new membership! 🎉`);
        updateUser({ ...user, membership_type: membershipId, loyalty_points: (user?.loyalty_points || 0) + 500 });
      } else {
        toast.error('Subscription failed');
      }
    } catch (error) {
      toast.error('Subscription failed');
    } finally {
      setSubscribing(null);
    }
  };

  const getTierIcon = (name) => {
    if (name.toLowerCase().includes('elite')) return Crown;
    if (name.toLowerCase().includes('pro')) return Zap;
    return Star;
  };

  const getTierStyle = (name) => {
    if (name.toLowerCase().includes('elite')) return 'border-secondary shadow-glow';
    if (name.toLowerCase().includes('pro')) return 'border-primary shadow-glow-teal';
    return 'border-border';
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="memberships-page">
        <div className="text-center mb-12">
          <h1 className="font-heading text-3xl lg:text-4xl font-bold mb-4">Membership Plans</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Unlock exclusive benefits, priority booking, and discounts with our membership plans.
          </p>
        </div>

        {/* Current Membership */}
        {user?.membership_type && (
          <Card className="mb-8 border-primary bg-primary/5">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Crown className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-lg">Current Plan</p>
                    <p className="text-muted-foreground">
                      {memberships.find(m => m.membership_id === user.membership_type)?.name || 'Active Membership'}
                    </p>
                  </div>
                </div>
                <Badge className="bg-primary text-white">Active</Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Membership Cards */}
        {loading ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => <div key={i} className="h-96 bg-muted animate-pulse rounded-xl"></div>)}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {memberships.map((membership) => {
              const Icon = getTierIcon(membership.name);
              const tierStyle = getTierStyle(membership.name);
              const isCurrentPlan = user?.membership_type === membership.membership_id;
              
              return (
                <Card 
                  key={membership.membership_id} 
                  className={`relative overflow-hidden ${tierStyle} ${isCurrentPlan ? 'ring-2 ring-primary' : ''}`}
                  data-testid={`membership-${membership.membership_id}`}
                >
                  {membership.name.toLowerCase().includes('pro') && (
                    <div className="absolute top-0 right-0 bg-primary text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                      Popular
                    </div>
                  )}
                  
                  <CardHeader className="text-center pb-4">
                    <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-7 w-7 text-primary" />
                    </div>
                    <CardTitle className="font-heading text-xl">{membership.name}</CardTitle>
                    <CardDescription>{membership.description}</CardDescription>
                  </CardHeader>
                  
                  <CardContent className="space-y-6">
                    {/* Pricing */}
                    <div className="text-center">
                      <div className="flex items-baseline justify-center">
                        <span className="text-3xl font-heading font-bold">₱{membership.price_monthly}</span>
                        <span className="text-muted-foreground ml-1">/month</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        or ₱{membership.price_yearly}/year (save 17%)
                      </p>
                    </div>

                    {/* Features */}
                    <ul className="space-y-3">
                      {membership.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm">
                          <Check className="h-4 w-4 mr-2 text-primary flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA */}
                    {isCurrentPlan ? (
                      <Button disabled className="w-full">
                        Current Plan
                      </Button>
                    ) : (
                      <div className="space-y-2">
                        <Button
                          onClick={() => handleSubscribe(membership.membership_id, 'monthly')}
                          disabled={subscribing === `${membership.membership_id}-monthly`}
                          className="w-full btn-primary"
                          data-testid={`subscribe-${membership.membership_id}-monthly`}
                        >
                          {subscribing === `${membership.membership_id}-monthly` ? 'Processing...' : 'Subscribe Monthly'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleSubscribe(membership.membership_id, 'yearly')}
                          disabled={subscribing === `${membership.membership_id}-yearly`}
                          className="w-full"
                          data-testid={`subscribe-${membership.membership_id}-yearly`}
                        >
                          {subscribing === `${membership.membership_id}-yearly` ? 'Processing...' : 'Subscribe Yearly'}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Benefits Section */}
        <div className="mt-16 text-center">
          <h2 className="font-heading text-2xl font-bold mb-6">Why Join?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Gift className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Exclusive Discounts</h3>
              <p className="text-sm text-muted-foreground">Get Exclusive discounts on bookings and equipment rentals</p>
            </div>
            <div className="p-6">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Priority Booking</h3>
              <p className="text-sm text-muted-foreground">Book courts before they're available to non-members</p>
            </div>
            <div className="p-6">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Star className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Loyalty Points</h3>
              <p className="text-sm text-muted-foreground">Earn loyalty points when you subscribe</p>
            </div>
          </div>
        </div>

        {/* Mock Payment Notice */}
        <Card className="mt-8 border-secondary bg-secondary/10">
          <CardContent className="p-4 text-center">
            <p className="text-sm">
              <strong>Test Mode:</strong> Payment is mocked. Subscriptions are simulated for demo purposes.
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default MembershipsPage;
